import { useMemo } from 'react';
import styled from 'styled-components';
import { FlexCenter, Flex } from '@elementary/common-ui/components';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import {
  TREE_NODE,
  TREE_TYPE,
  TREE_VIEW_BY,
  TEST_RESULTS_TABLE_FILTERS,
} from '@elementary/common-ui/constants/urlParams';
import { Tooltip, Typography } from '@mui/material';
import { AssetData, DimensionTotals } from 'shared/hooks/useAssetData/types';
import useNavigateWithEnv from 'shared/hooks/useNavigateWithEnv';
import { fontSize, radius, space } from '@elementary/common-ui/style/utils';

const TestChip = styled(FlexCenter)<{
  $color: 'default' | 'error' | 'success' | 'warning';
  $disabled: boolean;
}>`
  padding: ${space('xxsPlus', 'sm')};
  border-radius: ${radius('capsule')};
  gap: ${space('smMinus')};
  white-space: nowrap;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  background-color: ${({ theme, $color }) => {
    if ($color === 'error') return theme.palette.status.error.bg;
    if ($color === 'success') return theme.palette.status.success.bg;
    if ($color === 'warning') return theme.palette.status.warning.bg;
    return theme.palette.surface.muted;
  }};
  & > .MuiStack-root {
    background-color: ${({ theme, $color }) => {
      if ($color === 'error') return theme.palette.status.error.soft;
      if ($color === 'success') return theme.palette.status.success.main;
      if ($color === 'warning') return theme.palette.status.warning.strong;
      return theme.palette.chart.idle;
    }};
  }
`;

const Dot = styled(Flex)`
  height: 8px;
  width: 8px;
  border-radius: ${radius('round')};
`;

const QUALITY_DIMENSIONS = [
  { key: 'completeness', title: 'Completeness' },
  { key: 'uniqueness', title: 'Uniqueness' },
  { key: 'freshness', title: 'Freshness' },
  { key: 'validity', title: 'Validity' },
  { key: 'accuracy', title: 'Accuracy' },
  { key: 'consistency', title: 'Consistency' },
  { key: 'other', title: 'Other' },
];

const EMPTY_DIMENSION_TOTALS: DimensionTotals = {
  passed: 0,
  warnings: 0,
  failures: 0,
  errors: 0,
  skipped: 0,
};

type DimChipColor = 'default' | 'error' | 'success' | 'warning';

const getDimChipColor = (entry: DimensionTotals): DimChipColor => {
  if (entry.failures + entry.errors > 0) return 'error';
  if (entry.warnings > 0) return 'warning';
  if (entry.passed > 0) return 'success';
  return 'default';
};

const getDimChipTooltip = (entry: DimensionTotals): string => {
  if (entry.failures + entry.errors > 0) return 'Failed';
  if (entry.warnings > 0) return 'Warning';
  if (entry.passed > 0) return 'Passed';
  if (entry.skipped > 0) return 'Skipped';
  return 'No tests';
};

const isDimChipClickable = (entry: DimensionTotals): boolean =>
  entry.passed +
    entry.warnings +
    entry.failures +
    entry.errors +
    entry.skipped >
  0;

type Props = {
  assetId: string;
  testExecutionsByQualityDimension: AssetData['testExecutionsByQualityDimension'];
};

const QualityDimensionChips = ({
  assetId,
  testExecutionsByQualityDimension,
}: Props) => {
  const navigate = useNavigateWithEnv();
  const [searchParams] = useSearchParams();
  const { formatUrlParam } = useSelectedTreeItem();

  const dimensionTotalsMap = useMemo(() => {
    if (!testExecutionsByQualityDimension) return null;
    return new Map(
      testExecutionsByQualityDimension.map((e) => [
        e.dimension.toLowerCase(),
        e.totals,
      ]),
    );
  }, [testExecutionsByQualityDimension]);

  const chips = useMemo(
    () =>
      QUALITY_DIMENSIONS.map(({ key, title }) => {
        const entry = dimensionTotalsMap?.get(key) ?? EMPTY_DIMENSION_TOTALS;
        const color = getDimChipColor(entry);
        const tooltip = getDimChipTooltip(entry);
        const clickable = isDimChipClickable(entry);

        const params = new URLSearchParams();
        const treeType = searchParams.get(TREE_TYPE);
        const treeViewBy = searchParams.get(TREE_VIEW_BY);
        if (treeType) params.set(TREE_TYPE, treeType);
        if (treeViewBy) params.set(TREE_VIEW_BY, treeViewBy);
        const urlParam = formatUrlParam(assetId);
        if (urlParam) params.set(TREE_NODE, urlParam);
        if (clickable) {
          params.set(
            TEST_RESULTS_TABLE_FILTERS,
            JSON.stringify([
              {
                property: 'qualityDimension',
                operator: 'equals',
                value: [key === 'other' ? '' : key],
              },
            ]),
          );
        }

        return { key, title, color, tooltip, clickable, params };
      }),
    [searchParams, dimensionTotalsMap, formatUrlParam, assetId],
  );

  return (
    <>
      {chips.map(({ key, title, color, tooltip, clickable, params }) => (
        <Tooltip key={key} title={tooltip}>
          <TestChip
            $color={color}
            $disabled={!clickable}
            onClick={() => {
              if (clickable)
                navigate(`/report/test-results?${params.toString()}`);
            }}
          >
            <Typography sx={{ fontSize: fontSize('sm') }}>{title}</Typography>
            <Dot color={color} />
          </TestChip>
        </Tooltip>
      ))}
    </>
  );
};

export default QualityDimensionChips;
